import {
  crmPipelineApi,
  expressionDefinitionApi,
  loanPipelinesApi,
  loanProcessingDefinitionsApi,
  subCompanyPipelineApi,
  taskApi,
} from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { LoanPipelineNodeType, LoanPipelineRepresentationViewType } from 'constants/loanPipelines';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

const pipelinesSchema = new schema.Entity('data', {}, { idAttribute: 'id' });

export const PIPELINES_ACTION_TYPE = createNamedActions('pipelines', {
  GET_PIPELINES: 'GET_PIPELINES',
  GET_PIPELINES_SCHEMA: 'GET_PIPELINES_SCHEMA',
  GET_PIPELINES_COLUMN_DEFINITIONS: 'GET_PIPELINES_COLUMN_DEFINITIONS',
  CREATE_PIPELINE: 'CREATE_PIPELINE',
  EDIT_PIPELINE: 'EDIT_PIPELINE',
  SET_PIPELINES: 'SET_PIPELINES',
  MERGE_PIPELINES: 'MERGE_PIPELINES',
  DELETE_PIPELINE: 'DELETE_PIPELINE',
});

export const getPipelinesAction = catchAsyncActionError(({ includes }) => async dispatch => {
  const { data } = await loanPipelinesApi.getLoanPipelines({
    includes,
  });

  return dispatch({
    type: PIPELINES_ACTION_TYPE.GET_PIPELINES,
    payload: normalize(data, [pipelinesSchema]).entities.data,
  });
});

export const getPipelinesSchemaAction = catchAsyncActionError(() => async dispatch => {
  const [loansSchemaResponse, crmSchemaResponse, subCompanySchemaResponse] = await Promise.all([
    expressionDefinitionApi.getLoanPipelinesExpressionDefinition(),
    crmPipelineApi.getExpressionDefinitions().catch(() => ({ data: null })),
    subCompanyPipelineApi.getExpressionDefinitions().catch(() => ({ data: null })),
  ]);

  const mergedData = {
    ...loansSchemaResponse.data,
    ...(crmSchemaResponse.data && { contactPipelinesSchema: crmSchemaResponse.data }),
    ...(subCompanySchemaResponse.data && {
      subCompanyPipelinesSchema: subCompanySchemaResponse.data,
    }),
  };

  return dispatch({
    type: PIPELINES_ACTION_TYPE.GET_PIPELINES_SCHEMA,
    payload: mergedData,
  });
});

export const getPipelinesColumnDefinitionsAction = catchAsyncActionError(() => async dispatch => {
  const [loansResponse, crmResponse] = await Promise.all([
    loanPipelinesApi.getLoanPipelinesColumnDefinitions(),
    crmPipelineApi.getColumnDefinitions().catch(() => ({ data: {} })),
  ]);

  const mergedData = { ...loansResponse.data, ...crmResponse.data };

  return dispatch({
    type: PIPELINES_ACTION_TYPE.GET_PIPELINES_COLUMN_DEFINITIONS,
    payload: mergedData,
  });
});

const validatePipelinesExpressionAction = catchAsyncActionError(params => async dispatch => {
  if (!Object.values(LoanPipelineRepresentationViewType).includes(params.representationView.type)) {
    dispatch(showMessageAction({ message: 'Invalid representation view', variant: 'error' }));

    return false;
  }

  const { expression, representationView, outputRule } = params;

  const { data } = await loanProcessingDefinitionsApi.validatePipelinesPotentialExpression({
    expression: expression?.value,
    pipelineType: representationView.type,
    sortingExpressions:
      outputRule &&
      outputRule.sortingCriteria?.map(({ criterion, orderNumber, typeOfValue }) => ({
        orderNumber,
        type: typeOfValue,
        expression: criterion?.value,
      })),
  });

  if (!data) {
    dispatch(showMessageAction({ message: 'Pipeline expression is not valid', variant: 'error' }));

    return false;
  }

  return true;
});

export const createPipelineAction = catchAsyncActionError(params => async dispatch => {
  const shouldSkipExpressionValidation =
    params.nodeType === LoanPipelineNodeType.GROUP ||
    params.representationView?.type === LoanPipelineRepresentationViewType.EXTERNAL_DATA;

  const isValid =
    shouldSkipExpressionValidation || (await dispatch(validatePipelinesExpressionAction(params)));

  if (!isValid) {
    return;
  }

  const { data } = await loanPipelinesApi.createLoanPipeline(params);

  return dispatch({
    type: PIPELINES_ACTION_TYPE.CREATE_PIPELINE,
    payload: data,
  });
});

export const editPipelineAction = catchAsyncActionError(params => async dispatch => {
  const shouldSkipExpressionValidation =
    params.nodeType === LoanPipelineNodeType.GROUP ||
    params.representationView?.type === LoanPipelineRepresentationViewType.EXTERNAL_DATA;

  const isValid =
    shouldSkipExpressionValidation || (await dispatch(validatePipelinesExpressionAction(params)));

  if (!isValid) {
    return;
  }

  const { data } = await loanPipelinesApi.editLoanPipeline(params);

  return dispatch({
    type: PIPELINES_ACTION_TYPE.EDIT_PIPELINE,
    payload: data,
  });
});

export const setPipelinesAction = list => ({
  type: PIPELINES_ACTION_TYPE.SET_PIPELINES,
  payload: normalize(list, [pipelinesSchema]).entities.data,
});

// Upserts each entry of `list` into the existing pipelines map — unlike setPipelinesAction
// (a full replace), this never drops pipelines that aren't part of the reorder. Required for
// the tree's sibling-scoped reorder, which only touches the moved node's old + new siblings.
const mergePipelinesAction = list => ({
  type: PIPELINES_ACTION_TYPE.MERGE_PIPELINES,
  payload: list,
});

// `list` is only the pipelines that actually changed (fresh 1-based orderNumber per parent
// scope, plus parentId on a re-parent) — never the full pipelines collection. Sending the
// whole list would forward every other pipeline's orderNumber verbatim, including any legacy
// orderNumber:0 the backend's PipelineOrderDto rejects (DEV-4530 render-verify finding).
export const editPipelinesOrder = catchAsyncActionError(list => async dispatch => {
  dispatch(mergePipelinesAction(list));

  await loanPipelinesApi.editLoanPipelinesOrder(
    list.map(({ id, parentId, orderNumber }) => ({ id, parentId: parentId ?? null, orderNumber })),
  );
});

export const deletePipelineAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanPipelinesApi.deleteLoanPipeline(params);

  return dispatch({
    type: PIPELINES_ACTION_TYPE.DELETE_PIPELINE,
    payload: data,
    meta: params,
  });
});

export const rejectPipelineTaskAction = catchAsyncActionError(params => async () => {
  const { data } = await taskApi.rejectTask(params);

  return data;
});

export const approvePipelineTaskAction = catchAsyncActionError(params => async () => {
  const { data } = await taskApi.approveTask(params);

  return data;
});

export const updatePipelineTaskAction = catchAsyncActionError(params => async () => {
  const { data } = await taskApi.updateTask(params);

  return data;
});
